
import React, { useCallback } from 'react';

import { getWindowWidth, isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { IconReactions } from '../../../base/icons';
import { getLocalParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import ToolbarButton from '../../../toolbox/components/web/ToolbarButton';
import { toggleReactionsMenuVisibility } from '../../actions.web';
import { type ReactionEmojiProps } from '../../constants';
import { getReactionsQueue, isReactionsEnabled } from '../../functions.any';
import { getReactionsMenuVisibility } from '../../functions.web';

import ReactionEmoji from './ReactionEmoji';
import ReactionsMenuPopup from './ReactionsMenuPopup';

type Props = {

    /**
     * Whether or not reactions are enabled.
     */
    _reactionsEnabled: Boolean,

    /**
     * Redux dispatch function.
     */
    dispatch: Function,

    /**
     * Click handler for raise hand functionality.
     */
    onClick: Function,

    /**
     * Whether or not the reactions menu is open.
     */
    isOpen: boolean,

    /**
     * Whether or not it's a mobile browser.
     */
    isMobile: boolean,

    /**
     * Whether or not the local participant's hand is raised.
     */
    raisedHand: boolean,

    /**
     * The array of reactions to be displayed.
     */
    reactionsQueue: Array<ReactionEmojiProps>,

    /**
     * Used for translation.
     */
    t: Function
};

/**
 * Button used for the reactions menu.
 *
 * @returns {ReactElement}
 */
function ReactionsMenuButton({
    _reactionsEnabled,
    dispatch,
    isOpen,
    reactionsQueue,
    t,
    conferenceView
}: Props) {
    const toggleReactionsMenu = useCallback(() => {
        dispatch(toggleReactionsMenuVisibility());
    }, [ dispatch ]);

    const reactionMenuButton = (<ToolbarButton
        accessibilityLabel = { t('toolbar.accessibilityLabel.reactionsMenu') }
        icon = { IconReactions }
        onClick = { toggleReactionsMenu }
        position = { conferenceView === 'fixed' ? 'top' : 'left' }
        toggled = { isOpen }
        tooltip = { t(`toolbar.${isOpen ? 'closeReactionsMenu' : 'openReactionsMenu'}`) } />);

    return (
        <div className = { `reactions-menu-popup-container ${getWindowWidth() < 805 ? 'reactions-menu-mobile-parent' : conferenceView === 'float' ? 'reactions-menu-float-parent' : ''}` }>
            <ReactionsMenuPopup>
                {_reactionsEnabled && reactionMenuButton}
            </ReactionsMenuPopup>
            {reactionsQueue.map(({ reaction, uid }, index) => (<ReactionEmoji
                index = { index }
                key = { uid }
                reaction = { reaction }
                uid = { uid } />))}
        </div>
    );
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const localParticipant = getLocalParticipant(state);

    return {
        _reactionsEnabled: isReactionsEnabled(state),
        isOpen: getReactionsMenuVisibility(state),
        isMobile: isMobileBrowser(),
        reactionsQueue: getReactionsQueue(state),
        raisedHand: localParticipant.raisedHand
    };
}

export default translate(connect(mapStateToProps)(ReactionsMenuButton));
